/**
 * Die Gewichtsbilanz einer Verpackungshierarchie -- an einer Stelle.
 *
 * DER FEHLER, DER DIESE DATEI NOETIG MACHT
 *
 * Die Bilanz zog von JEDER Ebene das Produktgewicht ab:
 *
 *     Masterkarton 19,10 kg − 6 × 2,75 kg = 2,60 kg Verpackung
 *
 * Am Referenzartikel TP154-4KD2D stimmt das nicht. Im Masterkarton liegen
 * keine sechs nackten Leuchten, sondern sechs INNENKARTONS zu je 3,05 kg:
 *
 *     Masterkarton 19,10 kg − 6 × 3,05 kg = 0,80 kg Verpackung
 *
 * Die Differenz von 1,8 kg ist nicht Kartonage, sondern sechsmal die
 * Innenverpackung -- die eine Ebene tiefer bereits gezaehlt wurde. Wer danach
 * disponiert oder eine VerpackungsG-Abgabe daraus rechnet, zahlt sie doppelt.
 *
 * DIE REGEL
 *
 * Von jeder Verpackungsstufe wird das Gewicht der DIREKT enthaltenen realen
 * Einheit abgezogen -- nicht pauschal das Produkt und nicht pauschal der
 * Innenkarton. Was direkt enthalten ist, sagt die Hierarchie:
 *
 *     Produkt → Innenkarton → Masterkarton
 *
 * Gibt es keinen Innenkarton, ist der Masterkarton die erste Stufe ueber dem
 * Produkt und enthaelt Produkte. Es wird KEINE Innenkartonstufe erfunden, nur
 * damit die Rechnung eine Zwischenstufe hat -- die fehlende Stufe bleibt
 * schlicht nicht vorhanden.
 *
 * WARUM IN `shared` UND NICHT IN DER OBERFLAECHE
 *
 * Dieselbe Rechnung wird an drei Stellen gebraucht: in der Gewichtsbilanz, in
 * der Ableitung des Restmaterials und im Materialanteil je verkauftem Artikel.
 * Dreimal geschrieben waeren es beim naechsten Mal drei verschiedene Regeln --
 * und genau so ist der Fehler oben entstanden.
 *
 * ALLE ZAHLEN IN GRAMM
 *
 * Bruttogewichte stehen in Kilogramm, Materialien in Gramm. Gerechnet wird
 * durchgehend in Gramm; gerundet wird auf Milligramm, weil `2.75 * 1000` in
 * Gleitkomma sonst 2750.0000000000005 ergibt und die Differenz am Ende als
 * „Rest" auftaucht, den niemand gewogen hat.
 */
/** Die Stufen, in der Reihenfolge von innen nach aussen. */
export const VERPACKUNGSSTUFEN = ["inner", "master"];
const runde = (n) => Math.round(n * 1000) / 1000;
/**
 * Die Toleranz einer Stufe.
 *
 * Am TP154 stehen 300 g berechnete Innenverpackung gegen 299 g gewogenes
 * Material. Das eine Gramm ist keine Luecke in den Daten, sondern der
 * Unterschied zwischen zwei Waagen -- und eine Oberflaeche, die daraus einen
 * Fehler macht, wird beim dritten Mal nicht mehr gelesen.
 *
 * Ein Prozent der berechneten Verpackungsmasse, mindestens aber ein Gramm:
 * die Toleranz waechst mit der Menge, und bei kleinen Verpackungen faellt sie
 * nicht unter die Aufloesung der Waage. Das ist bewusst EINE Regel und keine
 * Toleranzarchitektur -- es gibt bisher keine, und dieser Block baut keine.
 */
export function toleranzG(verpackungG) {
    return Math.max(1, runde(Math.abs(verpackungG) * 0.01));
}
/**
 * Die Bilanz aller Stufen, in der Reihenfolge von innen nach aussen.
 *
 * `produktgewichtKg` ist das Merkmal `weight` der SKU. Fehlt es, gibt es
 * nichts zu verrechnen: eine Bilanz, die das Produkt mit 0 kg ansetzt,
 * erklaerte das gesamte Bruttogewicht zur Verpackung.
 */
export function verpackungsbilanz(ebenen, produktgewichtKg) {
    if (produktgewichtKg === null || produktgewichtKg === "")
        return [];
    const produktG = runde(Number(produktgewichtKg) * 1000);
    if (!Number.isFinite(produktG))
        return [];
    const inner = ebenen.find((l) => l.type === "inner") ?? null;
    const sortiert = [...ebenen].sort((a, b) => VERPACKUNGSSTUFEN.indexOf(a.type)
        - VERPACKUNGSSTUFEN.indexOf(b.type));
    return sortiert.flatMap((l) => {
        if (l.grossWeightKg === null)
            return [];
        const bruttoG = runde(Number(l.grossWeightKg) * 1000);
        if (!Number.isFinite(bruttoG))
            return [];
        /*
         * WAS LIEGT DIREKT DARIN -- UND WIE VIELE DAVON?
         *
         * Der Masterkarton enthaelt Innenkartons, WENN es welche gibt -- sonst
         * Produkte. Der Innenkarton enthaelt immer Produkte. Mehr Faelle hat das
         * Modell heute nicht; kaeme eine dritte Stufe dazu, waere sie hier eine
         * Zeile und nicht eine zweite Rechnung anderswo.
         *
         * DIE STUECKZAHL IST NICHT DIE ZAHL DER INNENKARTONS.
         *
         * `unit_count` zaehlt die VERKAUFSEINHEITEN in einer Einheit dieser Stufe
         * -- laut Migration 0012 ausdruecklich „nicht der naechstkleineren Ebene".
         * Am TP154 steht am Masterkarton also 6 (sechs Leuchten), und weil in
         * jedem Innenkarton genau eine liegt, sind das auch sechs Innenkartons.
         * Laegen zwei Leuchten in jedem Innenkarton, stuende dort 12 -- und
         * abzuziehen waeren SECHS Innenkartons, nicht zwoelf. Wer hier `unitCount`
         * direkt mit dem Innenkartongewicht multipliziert, zaehlt die
         * Innenverpackung doppelt so oft wie sie vorkommt.
         */
        const direktInner = l.type === "master" && inner !== null && inner.grossWeightKg !== null
            && inner.unitCount > 0;
        const einheitG = direktInner ? runde(Number(inner.grossWeightKg) * 1000) : produktG;
        const inhaltQuelle = direktInner ? "inner" : "produkt";
        /*
         * Geht die Teilung nicht auf, widersprechen sich die beiden Stufen -- ein
         * Masterkarton zu fuenf Leuchten ueber Innenkartons zu je zweien kann es
         * nicht geben. Gerechnet wird dann mit dem krummen Quotienten, und die
         * Bilanz schreibt ihn hin („2,5 x Innenkarton"). Das ist Absicht: eine
         * sichtbar unmoegliche Zahl fuehrt zur Klaerung, ein stiller Rueckfall auf
         * das Produktgewicht ergaebe genau den plausibel aussehenden Wert, dessen
         * Korrektur der Anlass dieser Datei ist.
         */
        const inhaltStueck = direktInner ? runde(l.unitCount / inner.unitCount) : l.unitCount;
        const inhaltG = runde(einheitG * inhaltStueck);
        const verpackungG = runde(bruttoG - inhaltG);
        const erfasstG = runde(l.materials.reduce((n, m) => n + Number(m.weightG), 0));
        const restG = runde(verpackungG - erfasstG);
        /* Produkte je Einheit ist `unitCount` selbst -- siehe oben. */
        const produkteJeEinheit = l.unitCount;
        const tol = toleranzG(verpackungG);
        return [{
                ebene: l, bruttoG, inhaltG, inhaltQuelle, verpackungG, erfasstG, restG,
                produkteJeEinheit, inhaltStueck,
                innerhalbToleranz: l.materials.length > 0 && Math.abs(restG) <= tol,
                toleranzG: tol,
            }];
    });
}
/** Die Bilanz EINER Stufe -- fuer Aufrufer, die nur eine brauchen. */
export function ebenenbilanz(ebenen, ebeneId, produktgewichtKg) {
    return verpackungsbilanz(ebenen, produktgewichtKg).find((b) => b.ebene.id === ebeneId) ?? null;
}
/**
 * Der noch keinem Material zugeordnete Rest EINER Stufe -- oder `null`.
 *
 * Das ist die Menge, die die Oberflaeche zur Zuordnung anbietet: „800 g nicht
 * zugeordnet, woraus besteht das?". Sie wird NICHT eingegeben, sondern
 * gerechnet -- wer sie abtippen muesste, koennte sich vertippen, und niemand
 * saehe es.
 *
 * `null` heisst: es gibt nichts zuzuordnen. Entweder fehlt eine Zahl, oder
 * der Rest ist null oder negativ. Ein negativer Rest ist ein Widerspruch in
 * den Messwerten und keine Materialmenge; er wird in der Bilanz gemeldet und
 * nicht hier ein zweites Mal.
 */
export function offenerRest(ebenen, ebeneId, produktgewichtKg) {
    const b = ebenenbilanz(ebenen, ebeneId, produktgewichtKg);
    if (b === null || !(b.restG > 0) || b.produkteJeEinheit <= 0)
        return null;
    return { restG: b.restG, jeProduktG: runde(b.restG / b.produkteJeEinheit) };
}
/*
 * ── `abgeleiteterRest` IST FORT (FOLLOW-UP 003 §3/§5) ───────────────
 *
 * HIER STAND die Regel, ob aus einem offenen Rest eine Materialaussage
 * werden darf: es musste eine Restmaterialzuordnung vorliegen, und --
 * bis Vorblock §14 -- durfte das Material nicht schon gewogen sein.
 *
 * Mit §3 gibt es diese Frage nicht mehr. Die Differenz ist
 * Eingabehilfe und Validierung, ausdruecklich kein eigener
 * Materialwert und keine gespeicherte Restwahrheit. Eine Funktion, die
 * aus ihr eine Materialaussage macht, hat damit keinen Gegenstand.
 *
 * An ihre Stelle tritt `offeneDifferenzen` weiter unten: dieselbe
 * Rechnung, andere Schlussfolgerung -- nicht „woraus besteht der
 * Rest", sondern „diese Stufe geht nicht auf".
 *
 * `offenerRest` BLEIBT: es sagt, WIEVIEL offen ist, und das ist die
 * Zahl, die die Oberflaeche als Differenz zeigt. `residual_material_id`
 * in der Datenbank bleibt ebenfalls -- zwei Stufen im Bestand tragen
 * eine Zuordnung, und sie bleibt lesbar (§6).
 */
/**
 * Die Stufen, deren Materialsumme NICHT dem Verpackungsgewicht entspricht.
 *
 * ── DIE FACHREGEL (BROWSER FOLLOW-UP 003 §1) ────────────────────────
 *
 * Ein Verpackungsstand darf nur abgeschlossen werden, wenn an JEDER
 * Stufe gilt:
 *
 *     Summe der Materialgewichte = errechnetes Verpackungsgewicht
 *
 * und zwar EXAKT. Am Innenkarton des TP154 sind das 300 g: 3,05 kg
 * brutto minus 2,75 kg Produkt. Erfasst sind 266 g Pappe und 33 g
 * Kunststoff, zusammen 299 g. Das genuegt nicht -- nicht 299 g, nicht
 * 301 g.
 *
 * ── WARUM DAS HIER STEHT UND NICHT IM FORMULAR ──────────────────────
 *
 * Weil es dieselbe Regel sein muss. Der Fachkern laesst eine
 * Bestaetigung nur zu, wenn diese Funktion leer zurueckgibt; die
 * Oberflaeche sperrt ihre Schaltflaeche mit derselben Funktion und
 * zeigt dieselbe Differenz. Zwei Rechnungen mit zwei Rundungen waeren
 * zwei Regeln, und die eine wuerde die andere irgendwann durchlassen.
 *
 * ── DIE TOLERANZ ENTSCHEIDET HIER NICHTS ────────────────────────────
 *
 * `toleranzG` und `innerhalbToleranz` bleiben als Auskunft bestehen --
 * sie sagen, wie weit zwei Waagen auseinanderliegen duerfen. Fuer den
 * ABSCHLUSS gilt sie ausdruecklich nicht: §2 sagt, HOMEBASE darf das
 * fehlende Gramm nicht als eigenstaendigen fachlichen Restzustand
 * akzeptieren, und eine Toleranz, die es doch akzeptiert, waere genau
 * das. Wer eine Differenz hat, korrigiert den Materialwert oder misst
 * neu (§2 A/B).
 *
 * Stufen ohne tragfaehige Bilanz -- kein Bruttogewicht, kein
 * Produktgewicht -- erscheinen NICHT: dort ist nichts zu vergleichen,
 * und eine erfundene Differenz waere schlimmer als keine.
 */
export function offeneDifferenzen(ebenen, produktgewichtKg) {
    return verpackungsbilanz(ebenen, produktgewichtKg)
        .filter((b) => b.restG !== 0)
        .map((b) => ({
        ebene: b.ebene, verpackungG: b.verpackungG,
        erfasstG: b.erfasstG, differenzG: b.restG,
    }));
}
/**
 * Der Materialeinsatz je verkauftem Produkt -- ueber alle Stufen zusammen.
 *
 * DER TEILER IST DIE ZAHL DER PRODUKTE.
 *
 * Das ist `unit_count`, und deshalb rechnet die Datenbank mit
 * `weight_g / unit_count` dieselbe Groesse (`per_unit_g` in
 * `core/logistics.ts`) -- die Spalte zaehlt Verkaufseinheiten, nicht Stuecke
 * der naechstkleineren Ebene. Hier steht sie trotzdem noch einmal, weil die
 * Summe ueber die Stufen und der abgeleitete Rest ohnehin gerechnet werden
 * muessen und ein zweiter Teiler an dieser Stelle der Anfang der naechsten
 * Abweichung waere.
 *
 * DER ZUGEORDNETE REST ZAEHLT MIT.
 *
 * Am TP154 sind 266 g Pappe am Innenkarton erfasst; die 800 g des
 * Masterkartons sind gerechnet und tragen nur eine Materialentscheidung. Wer
 * sie weglaesst, meldet 266 g Pappe je Produkt statt 399,33 g -- und meldet
 * damit rund ein Drittel der Verpackung nicht. Der Anteil wird deshalb
 * uebernommen, sobald `abgeleiteterRest` ihn freigibt, und traegt sein eigenes
 * Wort, damit er in keiner Meldung wie eine Wagung aussieht.
 */
export function materialJeProdukt(ebenen, produktgewichtKg) {
    const m = new Map();
    const nimm = (material, stufe, je, herkunft) => {
        const e = m.get(material) ?? { summe: 0, anteile: [] };
        e.summe = runde(e.summe + je);
        e.anteile.push({ stufe, grammJeProdukt: je, herkunft });
        m.set(material, e);
    };
    for (const b of verpackungsbilanz(ebenen, produktgewichtKg)) {
        if (b.produkteJeEinheit <= 0)
            continue;
        for (const mat of b.ebene.materials) {
            nimm(mat.material, b.ebene.type, runde(Number(mat.weightG) / b.produkteJeEinheit), "erfasst");
        }
        /*
         * ── DER REST IST KEIN MATERIALANTEIL MEHR (§3) ──────────────────
         *
         * HIER STAND: `abgeleiteterRest` liefern lassen und als Anteil mit
         * der Herkunft „abgeleitet" aufnehmen. Genau das ist mit §3
         * aufgehoben -- die Differenz ist Eingabehilfe und Validierung,
         * ausdruecklich KEIN eigener Materialwert und keine gespeicherte
         * Restwahrheit.
         *
         * DAS HAT EINE SICHTBARE FOLGE, und sie ist gewollt: am TP154 sind
         * 266 g Pappe am Innenkarton erfasst, die 800 g des Masterkartons
         * waren nur ZUGEORDNET, nicht gewogen. Die Pappe je Produkt sinkt
         * damit von 399,33 g auf 266 g -- bis jemand die 800 g als
         * Materialgewicht eintraegt. Die Luecke verschwindet nicht, sie
         * wird sichtbar: `offeneDifferenzen` nennt sie, und der Stand
         * laesst sich nicht abschliessen.
         *
         * Das ist der Unterschied, den §2 verlangt: eine Zahl, die jemand
         * eingetragen hat, statt einer, die HOMEBASE verteilt hat.
         */
    }
    return [...m.entries()].map(([material, e]) => ({
        material, grammJeProdukt: e.summe, anteile: e.anteile,
    }));
}
//# sourceMappingURL=verpackung.js.map